import {
  buildLiveTabHash,
  DEFAULT_SUBTAB,
  LIVE_MANAGEMENT_SUBTABS,
  type LiveManagementSubTab,
  type LiveTabSection,
} from '../screens/master-profile/live-tab/liveTabRoute';

/**
 * URL-псевдонимы: адреса, которые люди набирают и присылают, но которых нет в
 * таблице маршрутов.
 *
 * Зачем отдельный слой. Незнакомый путь React Navigation не отвергает — он
 * молча показывает начальный экран и переписывает адрес на «/». Снаружи это
 * выглядит так, будто страница есть, а внутри пользователь уже на главной: и
 * контекст страницы, который уходит Agapella, честно сообщает `route: '/'`,
 * `page_type: 'home'`. Так и было поймано QA на `/calendar` — контекст не
 * врал, его увели вместе с человеком.
 *
 * Поэтому такие адреса лучше разрешать явно, а не оставлять на молчаливый
 * откат: тогда и страница открывается та, и контекст описывает её же.
 */
export type PathAlias =
  /** Экран есть в таблице маршрутов — переходим внутри приложения. */
  | {
    kind: 'navigate';
    /** Куда переписать адрес в браузере. */
    path: string;
    /** Экран, на который перейти. */
    route: string;
    params?: Record<string, unknown>;
  }
  /**
   * Назначение адресуется #фрагментом — отдаём НАСТОЯЩИЙ редирект.
   *
   * Внутренним переходом сюда не попасть: React Navigation переписывает адрес
   * своим эффектом после дочерних и фрагмент при этом теряет — он не часть
   * состояния навигатора. А вот прямой заход по такому адресу приложение
   * разбирает правильно и давно (CORE-1131: фрагмент — единственный источник
   * раздела, и он же переживает перезагрузку). Редирект приводит человека
   * ровно к этому пути.
   */
  | { kind: 'redirect'; to: string };

/** `/x`, `/x/`, `/x?…` — но не `/xyz`: похожее начало пути не псевдоним. */
function isPath(p: string, base: string): boolean {
  return p === base || p.startsWith(`${base}/`) || p.startsWith(`${base}?`);
}

/**
 * CORE-1266 — разделы вкладки «Мои Live-сессии» адресуются #фрагментом внутри
 * дашборда (`/dashboard#rooms/management/series`), но живут в голове как
 * самостоятельные страницы: их набирают, кладут в закладки и присылают как
 * `/recordings`, `/management/series`, `/templates`. До этой правки такой
 * адрес уводил авторизованного мастера на главную — тем самым молчаливым
 * откатом, что описан в шапке файла.
 *
 * Фрагмент собирает {@link buildLiveTabHash}, а не строка здесь: грамматика
 * адреса у вкладки одна, и расходиться ей негде. Роль проверять не нужно и
 * нельзя — это делает сам дашборд, приводя раздел к тому, что человеку
 * принадлежит (CORE-1206: обычный пользователь попадёт на свои «Совместные
 * практики», а не в мастерскую панель с отказом сервера).
 */
function roomsSection(section: LiveTabSection, subTab: LiveManagementSubTab = DEFAULT_SUBTAB): PathAlias {
  return { kind: 'redirect', to: `/dashboard${buildLiveTabHash({ section, subTab })}` };
}

const ALIASES: Array<{ match: (path: string) => boolean; to: PathAlias }> = [
  {
    // «Лента» живёт вкладкой внутри дашборда, но раздавалась как отдельный
    // адрес — правило было здесь и раньше, просто вписанное в App.tsx.
    match: (p) => isPath(p, '/feed'),
    to: { kind: 'navigate', path: '/dashboard', route: 'Dashboard', params: { initialTab: 'feed' } },
  },
  {
    // Страница называется «Календарь», а адрес у неё — /schedule. Набирают и
    // присылают при этом /calendar, и до этой правки такой адрес уводил на
    // главную.
    match: (p) => isPath(p, '/calendar'),
    to: { kind: 'navigate', path: '/schedule', route: 'Schedule' },
  },
  {
    // CORE-1266 — записи эфиров.
    match: (p) => isPath(p, '/recordings'),
    to: roomsSection('recordings'),
  },
  // CORE-1266 — подвкладки «Управления». Идут ПЕРЕД общим `/management`:
  // правило выбирается первым совпавшим, а `/management/series` попадает и
  // под общее тоже.
  ...LIVE_MANAGEMENT_SUBTABS.map((subTab) => ({
    match: (p: string) => isPath(p, `/management/${subTab}`),
    to: roomsSection('management', subTab),
  })),
  {
    match: (p) => isPath(p, '/management'),
    to: roomsSection('management'),
  },
  {
    // CORE-1266 — шаблоны Live-сессий раздаются коротким адресом, а живут
    // подвкладкой «Управления».
    match: (p) => isPath(p, '/templates'),
    to: roomsSection('management', 'templates'),
  },
  {
    // CORE-1266 — создание офлайн-события. Экран настоящий, просто адрес у
    // него другой — `/events/create`.
    match: (p) => isPath(p, '/offline-events/create'),
    to: { kind: 'navigate', path: '/events/create', route: 'CreateEvent' },
  },
];

/** Куда вести с этого адреса, или null — если он и так настоящий. */
export function resolvePathAlias(pathname: string | undefined | null): PathAlias | null {
  const path = (pathname || '').trim();
  if (!path) return null;
  return ALIASES.find((a) => a.match(path))?.to ?? null;
}
